// src/pages/Admin/Adherents/AdherentDetails.jsx
import React from "react";
import { FaPhone, FaSms, FaEnvelope, FaEdit } from "react-icons/fa";

function getStatusClass(statut) {
  switch (statut) {
    case "Licencié":
      return "bg-green-300 text-slate-800";
    case "Licencié Hors Club":
      return "bg-green-200 text-slate-800";
    case "Inscrit":
      return "bg-yellow-300 text-slate-900";
    case "Cours d'essai":
      return "bg-red-300 text-slate-900";
    case "Licencié - Arrêt":
      return "bg-slate-300 text-slate-800";
    case "Ancien Licencié et Arrêt":
      return "bg-slate-300 text-slate-800";
    case "Ancien Licencié":
      return "bg-slate-900 text-white";
    case "Non Licencié - Arrêt":
      return "bg-slate-500 text-white";
    default:
      return "bg-slate-200 text-slate-800";
  }
}

const toAbsolute = (u) => {
  if (!u) return "";
  const s = String(u).trim();
  // déjà absolu, data URL ou blob
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:"))
    return s;
  // chemins d'assets front (ex: /img/...): on laisse tel quel
  if (!s.startsWith("/uploads")) return s;
  // fichiers uploadés côté API
  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${s}`;
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

const Field = ({ label, value }) => (
  <div className="space-y-1">
    <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {label}
    </div>
    <div className="text-sm text-slate-800 dark:text-slate-100 break-words">
      {value || "—"}
    </div>
  </div>
);

export default function AdherentDetails({
  adherent = {},
  showTeacherNotes = true,
  onEdit,
}) {
  const {
    photo_url,
    prenom,
    nom,
    statut_inscription,
    tel,
    tel_secondaire,
    email,
    email_secondaire,
    createdAt,
    representant_legal,
    adresse,
    cp,
    commune,
    genre,
    date_naissance,
    lieu_naissance,
    poids,
    licence_number,
    saison_active,
    notes_adherent,
    notes_enseignant,
    passsport_number,
    passclub32_number,
    citoyen_number,
    join_whatsapp,
    updatedAt, // utile pour cache-bust
  } = adherent || {};

  const hasTel = !!tel;
  const hasEmail = !!email;

  // URL absolue sûre pour l’image (gère http/https, /uploads/..., etc.)
  const absPhoto = toAbsolute(photo_url)
    ? toAbsolute(photo_url)
    : "/img/user_avatar.jpg";
  // cache-bust léger pour voir immédiatement la nouvelle photo après MAJ
  const cacheBust = updatedAt ? `?v=${new Date(updatedAt).getTime()}` : "";

  return (
    <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* Header dégradé */}
      <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className="text-white text-lg sm:text-xl font-semibold">
            {String(nom || "").toUpperCase()} {prenom || ""}
          </h2>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-semibold shadow ${getStatusClass(
              statut_inscription,
            )}`}
            title="Statut d'inscription"
          >
            {statut_inscription || "—"}
          </span>
        </div>
      </div>

      {/* Contenu */}
      <div className="p-5 sm:p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Colonne gauche : photo + actions */}
          <div className="lg:col-span-1">
            <div className="rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900">
              {/* Conteneur avec hauteur fixe pour un rendu stable */}
              <div className="w-full max-w-64 h-64">
                <img
                  src={`${absPhoto}${cacheBust}`}
                  alt={`${prenom || ""} ${nom || ""}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/img/user_avatar.jpg";
                  }}
                />
              </div>
            </div>

            {/* Actions rapides */}
            <div className="mt-4 flex flex-wrap gap-2">
              <a
                href={hasTel ? `tel:${tel}` : undefined}
                aria-disabled={!hasTel}
                className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm ring-1 shadow-sm transition
                  ${
                    hasTel
                      ? "bg-blue-600 text-white hover:bg-blue-700 ring-blue-300/40"
                      : "bg-slate-200 text-slate-500 ring-slate-300/40 cursor-not-allowed"
                  }`}
              >
                <FaPhone /> Appel
              </a>
              <a
                href={hasTel ? `sms:${tel}` : undefined}
                aria-disabled={!hasTel}
                className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm ring-1 shadow-sm transition
                  ${
                    hasTel
                      ? "bg-emerald-600 text-white hover:bg-emerald-700 ring-emerald-300/40"
                      : "bg-slate-200 text-slate-500 ring-slate-300/40 cursor-not-allowed"
                  }`}
              >
                <FaSms /> SMS
              </a>
              <a
                href={hasEmail ? `mailto:${email}` : undefined}
                aria-disabled={!hasEmail}
                className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm ring-1 shadow-sm transition
                  ${
                    hasEmail
                      ? "bg-indigo-600 text-white hover:bg-indigo-700 ring-indigo-300/40"
                      : "bg-slate-200 text-slate-500 ring-slate-300/40 cursor-not-allowed"
                  }`}
              >
                <FaEnvelope /> Mail
              </a>
            </div>
          </div>

          {/* Colonne droite : infos */}
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Coordonnées */}
            <div className="md:col-span-2">
              <div className="text-lg font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-2">
                Editer la fiche adherent
                {onEdit && (
                  <button
                    type="button"
                    onClick={() => onEdit(adherent)}
                    title="Modifier"
                    className="ml-2 inline-flex items-center p-1 rounded-md border border-transparent
               text-slate-500 hover:text-slate-700 hover:border-slate-300
               dark:text-slate-400 dark:hover:text-slate-200 dark:hover:border-slate-600
               transition"
                  >
                    <FaEdit className="text-[20px] text-primary" />
                    <span className="sr-only">Modifier</span>
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-100 dark:bg-slate-900/40">
                <Field label="Email" value={email} />
                <Field label="Email secondaire" value={email_secondaire} />
                <Field label="Téléphone" value={tel} />
                <Field label="Téléphone secondaire" value={tel_secondaire} />
                <Field
                  label="Adresse"
                  value={
                    [adresse, cp, commune].filter(Boolean).join(" ") || "—"
                  }
                />
                <Field label="Représentant légal" value={representant_legal} />
              </div>
            </div>

            {/* Informations personnelles */}
            <div className="md:col-span-1">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-2">
                Informations personnelles
              </div>
              <div className="grid grid-cols-1 gap-4 rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-100 dark:bg-slate-900/40">
                <Field label="Genre" value={genre} />
                <Field
                  label="Date de naissance"
                  value={fmtDate(date_naissance)}
                />
                <Field label="Lieu de naissance" value={lieu_naissance} />
                <Field label="Nationalité Française" value="France" />
                <Field label="Poids" value={poids ? `${poids} kg` : "—"} />
                <Field label="Créé le" value={fmtDate(createdAt)} />
              </div>
            </div>

            {/* Licence & Club */}
            <div className="md:col-span-1">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-2">
                Licence & club
              </div>
              <div className="grid grid-cols-1 gap-4 rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-100 dark:bg-slate-900/40">
                <Field label="N° de licence" value={licence_number} />
                <Field label="Saison active" value={saison_active} />
                <Field label="N° Pass'Sport" value={passsport_number} />
                <Field label="N° Pass'Club 32" value={passclub32_number} />
                <Field label="N° Citoyen" value={citoyen_number} />
                {join_whatsapp && (
                  <span className="inline-flex items-center gap-1 mt-2 text-green-600 dark:text-green-400 text-sm font-medium">
                    <FaSms /> Inscrit au groupe WhatsApp
                  </span>
                )}
              </div>
            </div>

            {/* Notes */}
            <div className="md:col-span-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300 mb-2">
                Notes
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-100 dark:bg-slate-900/40">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                    Notes adhérent
                  </div>
                  <div className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap">
                    {notes_adherent || "—"}
                  </div>
                </div>
                {showTeacherNotes && (
                  <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-4 bg-slate-100 dark:bg-slate-900/40">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                      Notes enseignant
                    </div>
                    <div className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap">
                      {notes_enseignant || "—"}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
